import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Users, Eye, Activity, Search, Building2, MapPin, Smartphone, Globe, RefreshCw, TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell } from "recharts";

const RANGE_OPTS = [
    { value: 1, label: "Bugün" },
    { value: 7, label: "7 gün" },
    { value: 30, label: "30 gün" },
    { value: 90, label: "90 gün" },
];

const DEVICE_COLORS = { desktop: "#0ea5e9", mobile: "#f59e0b", tablet: "#a855f7", unknown: "#94a3b8" };
const SOURCE_COLORS = { direct: "#64748b", google: "#0ea5e9", bing: "#0284c7", social: "#a855f7", youtube: "#dc2626", referral: "#f59e0b", internal: "#94a3b8", whatsapp: "#22c55e", telegram: "#0ea5e9" };

export default function AdminAnalyticsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();

    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [live, setLive] = useState({ online_now: 0, recent: [] });
    const [topHotels, setTopHotels] = useState([]);
    const [topDests, setTopDests] = useState([]);
    const [topPages, setTopPages] = useState([]);
    const [searches, setSearches] = useState([]);
    const [devices, setDevices] = useState({ devices: [], browsers: [] });
    const [sources, setSources] = useState([]);
    const [funnel, setFunnel] = useState({ steps: [] });

    const loadAll = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [ov, tp, th, td, sq, dv, sr, fn] = await Promise.all([
                api.get(`/admin/analytics/overview?days=${days}`),
                api.get(`/admin/analytics/top-pages?days=${days}&limit=10`),
                api.get(`/admin/analytics/top-hotels?days=${days}&limit=10`),
                api.get(`/admin/analytics/top-destinations?days=${days}&limit=10`),
                api.get(`/admin/analytics/searches?days=${days}&limit=15`),
                api.get(`/admin/analytics/devices?days=${days}`),
                api.get(`/admin/analytics/sources?days=${days}`),
                api.get(`/admin/analytics/funnel?days=${days}`),
            ]);
            setOverview(ov.data);
            setTopPages(tp.data || []);
            setTopHotels(th.data || []);
            setTopDests(td.data || []);
            setSearches(sq.data || []);
            setDevices(dv.data || {});
            setSources(sr.data || []);
            setFunnel(fn.data || { steps: [] });
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const loadLive = async () => {
        try {
            const r = await api.get("/admin/analytics/live");
            setLive(r.data);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin", "operations", "content"].includes(user.role)) return navigate("/login");
        loadAll();
        loadLive();
        // eslint-disable-next-line
    }, [user, authLoading, days]);

    useEffect(() => {
        const t = setInterval(loadLive, 20000);
        return () => clearInterval(t);
    }, []);

    const t = overview?.totals || {};
    const funnelBySlug = Object.fromEntries((funnel.steps || []).map((s) => [s.event_type, s]));

    return (
        <AdminLayout title="Site Trafiği">
            {/* Range switcher + live pill + refresh */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="flex items-center gap-1 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-1">
                    {RANGE_OPTS.map((o) => (
                        <button
                            key={o.value}
                            onClick={() => setDays(o.value)}
                            data-testid={`range-${o.value}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${days === o.value ? "bg-sky-500 text-white" : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                        >{o.label}</button>
                    ))}
                </div>

                <div data-testid="online-now" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                    </span>
                    {live.online_now} kişi şu an online
                </div>

                <button
                    onClick={() => { loadAll(); loadLive(); }}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-sky-100 dark:bg-sky-950/40 hover:bg-sky-200 dark:hover:bg-sky-900/60 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-900"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Yenile
                </button>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiCard icon={<Users className="h-4 w-4" />} label="Tekil Ziyaretçi" value={t.unique_visitors ?? "—"} color="sky" />
                <KpiCard icon={<Eye className="h-4 w-4" />} label="Sayfa Görüntüleme" value={t.page_views ?? "—"} color="indigo" />
                <KpiCard icon={<Activity className="h-4 w-4" />} label="Oturum" value={t.unique_sessions ?? "—"} color="emerald" />
                <KpiCard icon={<TrendingUp className="h-4 w-4" />} label="Toplam Olay" value={t.events ?? "—"} color="amber" />
            </div>

            {/* Time series */}
            <Card title="Günlük Trafik" subtitle={`Son ${days} gün · sayfa görüntüleme ve tekil ziyaretçi`}>
                <div className="h-72" data-testid="chart-by-day">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={overview?.by_day || []}>
                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Line type="monotone" dataKey="views" name="Görüntüleme" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="visitors" name="Ziyaretçi" stroke="#a855f7" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            {/* Funnel */}
            <Card title="Dönüşüm Hunisi" subtitle="Otel görüntüleme → tıklama → rezervasyon başlatma → tamamlama">
                <div className="grid grid-cols-4 gap-3 py-3">
                    {["hotel_view", "hotel_click", "booking_started", "booking_completed"].map((k, i) => {
                        const labels = { hotel_view: "Görüntüleme", hotel_click: "Tıklama", booking_started: "Başlatıldı", booking_completed: "Tamamlandı" };
                        const colors = ["bg-sky-100 text-sky-700", "bg-indigo-100 text-indigo-700", "bg-amber-100 text-amber-700", "bg-emerald-100 text-emerald-700"];
                        const step = funnelBySlug[k];
                        const prevCount = i === 0 ? null : funnelBySlug[["hotel_view", "hotel_click", "booking_started", "booking_completed"][i - 1]]?.count || 0;
                        const rate = (prevCount && step?.count) ? Math.round((step.count / prevCount) * 100) : null;
                        return (
                            <div key={k} className={`rounded-2xl p-4 ${colors[i]} relative`}>
                                <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">{labels[k]}</div>
                                <div className="mt-1 text-3xl font-black">{step?.count || 0}</div>
                                <div className="mt-1 text-[10px] opacity-70">{step?.visitors || 0} tekil</div>
                                {rate !== null && (
                                    <div className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-white text-[10px] font-bold">%{rate}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
                {/* Top Hotels */}
                <Card title="En Çok İlgi Gören Oteller" subtitle="Sayfa görüntüleme ve karttan tıklama">
                    <List
                        empty="Henüz veri yok"
                        items={topHotels.map((h) => ({
                            key: h.hotel_id,
                            icon: <Building2 className="h-3.5 w-3.5" />,
                            title: h.hotel_name || "—",
                            value: `${h.views} görüntüleme · ${h.clicks} tıklama`,
                            metric: h.visitors,
                        }))}
                    />
                </Card>

                {/* Top Destinations */}
                <Card title="Popüler Destinasyonlar" subtitle="Destinasyon sayfası görüntüleme sayıları">
                    <List
                        empty="Henüz veri yok"
                        items={topDests.map((d) => ({
                            key: d.slug,
                            icon: <MapPin className="h-3.5 w-3.5" />,
                            title: d.destination_name || d.slug,
                            value: `${d.views} görüntüleme`,
                            metric: d.visitors,
                        }))}
                    />
                </Card>

                {/* Top Pages */}
                <Card title="En Çok Görüntülenen Sayfalar" subtitle="Site içi tüm yollar">
                    <List
                        empty="Henüz veri yok"
                        items={topPages.map((p) => ({
                            key: p.path,
                            icon: <Eye className="h-3.5 w-3.5" />,
                            title: p.path,
                            value: `${p.views} görüntüleme`,
                            metric: p.visitors,
                        }))}
                    />
                </Card>

                {/* Searches */}
                <Card title="En Çok Aranan Kelimeler" subtitle="Misafir arama sorguları">
                    <List
                        empty="Henüz arama yapılmamış"
                        items={searches.map((s) => ({
                            key: s.query,
                            icon: <Search className="h-3.5 w-3.5" />,
                            title: s.query,
                            value: `${s.count} arama`,
                            metric: s.visitors,
                        }))}
                    />
                </Card>

                {/* Devices */}
                <Card title="Cihaz Dağılımı" subtitle="Ziyaretlerin cihaz türüne göre kırılımı">
                    <div className="h-56" data-testid="chart-devices">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={devices.devices || []} dataKey="views" nameKey="device" cx="50%" cy="50%" outerRadius={80} label>
                                    {(devices.devices || []).map((d) => (
                                        <Cell key={d.device} fill={DEVICE_COLORS[d.device] || "#94a3b8"} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                {/* Sources */}
                <Card title="Trafik Kaynakları" subtitle="Ziyaretçiler nereden geldi?">
                    <div className="h-56" data-testid="chart-sources">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={sources} layout="vertical">
                                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="source" tick={{ fontSize: 11 }} width={80} />
                                <Tooltip />
                                <Bar dataKey="views" name="Görüntüleme" radius={[0, 6, 6, 0]}>
                                    {sources.map((s) => <Cell key={s.source} fill={SOURCE_COLORS[s.source] || "#94a3b8"} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            {/* Live feed */}
            <Card title="Canlı Feed" subtitle="Son 5 dakika içindeki sayfa açılışları">
                <div className="max-h-80 overflow-y-auto" data-testid="live-feed">
                    {live.recent.length === 0 ? (
                        <div className="py-6 text-center text-sm text-slate-500">Şu an aktif ziyaretçi yok</div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
                                <tr>
                                    <th className="py-1.5 text-left">Zaman</th>
                                    <th className="py-1.5 text-left">Sayfa</th>
                                    <th className="py-1.5 text-left">Kaynak</th>
                                    <th className="py-1.5 text-left">Cihaz</th>
                                </tr>
                            </thead>
                            <tbody>
                                {live.recent.map((r, i) => (
                                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800/40">
                                        <td className="py-1.5 text-slate-500">{new Date(r.created_at).toLocaleTimeString("tr-TR")}</td>
                                        <td className="py-1.5 font-mono text-[11px] truncate max-w-[300px]">{r.path}</td>
                                        <td className="py-1.5"><SourcePill v={r.source} /></td>
                                        <td className="py-1.5"><DevicePill v={r.device} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </Card>
        </AdminLayout>
    );
}

function KpiCard({ icon, label, value, color }) {
    const palette = {
        sky: "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900 text-sky-700 dark:text-sky-300",
        indigo: "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-900 text-indigo-700 dark:text-indigo-300",
        emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300",
        amber: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300",
    }[color];
    return (
        <div data-testid={`kpi-${label}`} className={`rounded-2xl border p-4 ${palette}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-80">{icon} {label}</div>
            <div className="mt-1 text-2xl font-black">{value}</div>
        </div>
    );
}

function Card({ title, subtitle, children }) {
    return (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 mb-4">
            <div className="mb-3">
                <div className="font-display text-sm font-bold text-slate-900 dark:text-white">{title}</div>
                {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
            </div>
            {children}
        </div>
    );
}

function List({ items, empty }) {
    if (!items || items.length === 0) {
        return <div className="py-6 text-center text-sm text-slate-500">{empty}</div>;
    }
    return (
        <ul className="space-y-1.5">
            {items.map((it) => (
                <li key={it.key} className="flex items-center gap-3 py-1.5 border-b border-slate-100 dark:border-slate-800/40 last:border-0">
                    <div className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500">{it.icon}</div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{it.title}</div>
                        <div className="text-[10px] text-slate-500">{it.value}</div>
                    </div>
                    <div className="text-xs font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">{it.metric}<span className="ml-1 text-[9px] text-slate-400 font-normal">tekil</span></div>
                </li>
            ))}
        </ul>
    );
}

function SourcePill({ v }) {
    return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold" style={{ background: (SOURCE_COLORS[v] || "#94a3b8") + "22", color: SOURCE_COLORS[v] || "#334155" }}>{v || "direct"}</span>;
}

function DevicePill({ v }) {
    return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold" style={{ background: (DEVICE_COLORS[v] || "#94a3b8") + "22", color: DEVICE_COLORS[v] || "#334155" }}>{v || "?"}</span>;
}
